<template>
  <div class="relative">
    <!-- Date Input Trigger -->
    <div 
      @click="toggleCalendar" 
      class="relative bg-black/60 backdrop-blur-lg border border-white/20 rounded-2xl px-4 py-3 cursor-pointer hover:border-white/30 transition-all duration-200 transform hover:scale-[1.02]"
    >
      <div class="flex items-center justify-between">
        <div class="flex flex-col">
          <label class="text-xs font-medium text-gray-300 mb-1">{{ label }}</label>
          <span class="text-sm font-semibold text-white">
            {{ displayValue || placeholder }}
          </span>
        </div>
        <div class="w-8 h-8 bg-gradient-to-t from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
          <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      </div>
    </div>

    <!-- Calendar Popup -->
    <div 
      v-if="isOpen" 
      class="calendar-popup fixed inset-0 z-[2147483647] flex items-center justify-center"
      style="z-index: 2147483647 !important; position: fixed !important; top: 0 !important; left: 0 !important; right: 0 !important; bottom: 0 !important; isolation: isolate !important; transform: translateZ(0) !important;"
      @click="closeCalendar"
    >
      <!-- Backdrop -->
      <div class="absolute inset-0 bg-black bg-opacity-50" style="z-index: 2147483647 !important;"></div>
      
      <!-- Calendar Container -->
      <div 
        class="calendar-container relative bg-black/70 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-6 w-80 max-w-screen-sm"
        style="z-index: 2147483647 !important; position: relative !important;"
        @click.stop
      >
        <!-- Calendar Header -->
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-white">{{ label }}</h3>
          <button 
            @click="closeCalendar"
            class="text-gray-400 hover:text-white transition-colors"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Month Navigation -->
        <div class="flex items-center justify-between mb-4">
          <button 
            @click="previousMonth"
            class="p-2 hover:bg-white/10 rounded-xl transition-colors"
          >
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h4 class="text-sm font-medium text-white">
            {{ currentMonthYear }}
          </h4>
          <button 
            @click="nextMonth"
            class="p-2 hover:bg-white/10 rounded-xl transition-colors"
          >
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        <!-- Calendar Grid -->
        <div class="grid grid-cols-7 gap-1 mb-4">
          <!-- Day Headers -->
          <div 
            v-for="day in ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']" 
            :key="day"
            class="text-xs font-medium text-gray-300 text-center py-2"
          >
            {{ day }}
          </div>
          
          <!-- Calendar Days -->
          <div 
            v-for="day in calendarDays" 
            :key="day.date"
            @click="selectDate(day.date)"
            class="relative p-2 text-center cursor-pointer rounded-lg transition-colors"
            :class="getDayClasses(day)"
          >
            <span class="text-sm">{{ day.dayNumber }}</span>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex justify-end space-x-2 pt-4 border-t border-white/20">
          <button 
            @click="closeCalendar"
            class="px-4 py-2 text-sm font-medium text-gray-300 hover:bg-white/10 rounded-xl transition-colors"
          >
            Cancel
          </button>
          <button 
            @click="confirmDate"
            class="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 rounded-xl transition-all duration-200 border border-white/20"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'

export default {
  name: 'ModernDatePickerComposition',
  props: {
    value: {
      type: [Date, String],
      default: null
    },
    label: {
      type: String,
      default: 'Select Date'
    },
    placeholder: {
      type: String,
      default: 'Choose a date'
    },
    minDate: {
      type: Date,
      default: null
    },
    maxDate: {
      type: Date,
      default: null
    }
  },
  emits: ['input'],
  setup(props, { emit }) {
    // Reactive data
    const isOpen = ref(false)
    const currentDate = ref(props.value ? new Date(props.value) : new Date())
    const selectedDate = ref(props.value ? new Date(props.value) : null)
    const tempSelectedDate = ref(props.value ? new Date(props.value) : null)

    // Computed properties
    const currentMonthYear = computed(() => {
      return currentDate.value.toLocaleDateString('en-US', { 
        month: 'long', 
        year: 'numeric' 
      })
    })

    const displayValue = computed(() => {
      if (!selectedDate.value) return null
      return selectedDate.value.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric'
      })
    })

    const calendarDays = computed(() => {
      const year = currentDate.value.getFullYear()
      const month = currentDate.value.getMonth()
      const firstDay = new Date(year, month, 1)
      const startDate = new Date(firstDay)
      startDate.setDate(startDate.getDate() - firstDay.getDay())
      
      const days = []
      const currentDate = new Date(startDate)
      
      // Generate 6 weeks of days (42 days)
      for (let i = 0; i < 42; i++) {
        const dayNumber = currentDate.getDate()
        const isCurrentMonth = currentDate.getMonth() === month
        const isToday = isTodayDate(currentDate)
        const isSelected = tempSelectedDate.value && isSameDay(currentDate, tempSelectedDate.value)
        const isDisabled = isDateDisabled(currentDate)
        
        days.push({
          date: new Date(currentDate),
          dayNumber,
          isCurrentMonth,
          isToday,
          isSelected,
          isDisabled
        })
        
        currentDate.setDate(currentDate.getDate() + 1)
      }
      
      return days
    })

    // Methods
    const toggleCalendar = () => {
      isOpen.value = !isOpen.value
      if (isOpen.value) {
        tempSelectedDate.value = selectedDate.value ? new Date(selectedDate.value) : null
      }
    }

    const closeCalendar = () => {
      isOpen.value = false
    }

    const previousMonth = () => {
      currentDate.value = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth() - 1, 1)
    }

    const nextMonth = () => {
      currentDate.value = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth() + 1, 1)
    }

    const selectDate = (date) => {
      if (!isDateDisabled(date)) {
        tempSelectedDate.value = new Date(date)
      }
    }

    const confirmDate = () => {
      selectedDate.value = tempSelectedDate.value ? new Date(tempSelectedDate.value) : null
      emit('input', selectedDate.value)
      closeCalendar()
    }

    const getDayClasses = (day) => {
      const classes = []
      
      if (!day.isCurrentMonth) {
        classes.push('text-gray-400')
      } else if (day.isDisabled) {
        classes.push('text-gray-400 cursor-not-allowed')
      } else if (day.isSelected) {
        classes.push('bg-blue-500 text-white hover:bg-blue-600')
      } else if (day.isToday) {
        classes.push('bg-white/20 text-white hover:bg-white/30')
      } else {
        classes.push('text-white hover:bg-white/10')
      }
      
      return classes.join(' ')
    }

    const isTodayDate = (date) => {
      const today = new Date()
      return isSameDay(date, today)
    }

    const isSameDay = (date1, date2) => {
      return date1.getDate() === date2.getDate() &&
             date1.getMonth() === date2.getMonth() &&
             date1.getFullYear() === date2.getFullYear()
    }

    const isDateDisabled = (date) => {
      if (props.minDate && date < props.minDate) return true
      if (props.maxDate && date > props.maxDate) return true
      return false
    }

    // Watchers
    watch(() => props.value, (newVal) => {
      if (newVal) {
        currentDate.value = new Date(newVal)
        selectedDate.value = new Date(newVal)
        tempSelectedDate.value = new Date(newVal)
      }
    })

    // Lifecycle
    onMounted(() => {
      // Close calendar when clicking outside
      const handleClickOutside = (e) => {
        if (!e.target.closest('.relative')) {
          closeCalendar()
        }
      }
      
      document.addEventListener('click', handleClickOutside)
      
      // Cleanup function
      onBeforeUnmount(() => {
        document.removeEventListener('click', handleClickOutside)
      })
    })

    return {
      // Reactive data
      isOpen,
      currentDate,
      selectedDate,
      tempSelectedDate,
      
      // Computed
      currentMonthYear,
      displayValue,
      calendarDays,
      
      // Methods
      toggleCalendar,
      closeCalendar,
      previousMonth,
      nextMonth,
      selectDate,
      confirmDate,
      getDayClasses,
      isTodayDate,
      isSameDay,
      isDateDisabled
    }
  }
}
</script>

<style scoped>
/* Custom scrollbar for the calendar */
::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

/* Dark mode scrollbar */
.dark ::-webkit-scrollbar-thumb {
  background: #4b5563;
}

.dark ::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

/* Ensure calendar popup is always on top */
.calendar-popup {
  position: fixed !important;
  z-index: 9999999 !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
}

.calendar-container {
  position: relative !important;
  z-index: 9999999 !important;
}
</style> 